import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useConnectSlack } from '@/modules/integrations/hooks/useConnectSlack';
import { useSlackCallbackParams } from '@/modules/integrations/hooks/useSlackCallbackParams';
import { buttonClass } from '@/modules/signup-flow/components/layout/motion';
import { SignupFlowShell } from '@/modules/signup-flow/components/layout/SignupFlowShell';
import { StepActions } from '@/modules/signup-flow/components/layout/SignupLayoutParts';
import { StepHeader } from '@/modules/signup-flow/components/layout/StepHeader';
import { SlackLogo } from '@/modules/signup-flow/components/slackIntegration/SlackLogo';
import {
  SIGN_UP_FLOW_STEPS,
  signupFlowStep,
} from '@/modules/signup-flow/signupFlowSteps';
import { Button } from '@/modules/ui/components/button/Button';

/**
 * Sign-up Slack OAuth result, shown in the tab the Slack step opened (see
 * `SlackOauthService.BuildSuccessRedirectUri` in finlay-api). The sign-up tab
 * stays open and picks up the connection on focus, so this page only sends
 * the user back there. Skip lives on that tab's Slack step, not here, so the
 * two tabs can't disagree about the saved step.
 */
export const SlackCallback = () => {
  const { status, isCancelled } = useSlackCallbackParams();
  const connectSlack = useConnectSlack();
  const [connecting, setConnecting] = useState(false);

  if (status === 'success') {
    return (
      <SignupFlowShell>
        <div className="flex flex-col gap-8">
          <StepHeader
            step={signupFlowStep.slack}
            totalSteps={SIGN_UP_FLOW_STEPS}
            title="Slack connected"
            subtitle="You can close this tab and go back to Finlay to pick a channel."
          />
          <StepActions>
            {/* For when the sign-up tab is gone, or the browser blocked the new tab. */}
            <Button className={buttonClass} asChild>
              <Link to="/sign-up/get-started" replace>
                Continue in Finlay
              </Link>
            </Button>
          </StepActions>
        </div>
      </SignupFlowShell>
    );
  }

  const retry = () => {
    setConnecting(true);
    connectSlack('SignUp');
  };

  return (
    <SignupFlowShell>
      <div className="flex flex-col gap-8">
        <StepHeader
          step={signupFlowStep.slack}
          totalSteps={SIGN_UP_FLOW_STEPS}
          title={
            isCancelled ? 'Slack isn’t connected' : 'Slack connection failed'
          }
          subtitle={
            isCancelled
              ? 'You stopped before Slack finished connecting. Try again, or close this tab and go back to Finlay to skip it for now.'
              : 'We could not finish connecting Slack. Try again, or close this tab and go back to Finlay to skip it for now.'
          }
        />

        <StepActions>
          <Button
            variant="ghost"
            className={buttonClass}
            disabled={connecting}
            asChild
          >
            <Link to="/sign-up/get-started">Go back</Link>
          </Button>
          <Button className={buttonClass} loading={connecting} onClick={retry}>
            {!connecting && <SlackLogo className="size-3.5" />}
            {connecting ? 'Connecting' : 'Try again'}
          </Button>
        </StepActions>
      </div>
    </SignupFlowShell>
  );
};
